import React, { useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { toast } from "sonner";
import { PackagePlus, SlidersHorizontal, Trash2Icon, ArrowLeftRight, Trash, PackageMinus, Plus, FilePlus2 } from "lucide-react";
import { titleCase } from "../../lib/format";
import NewItemDialog from "./NewItemDialog";

const BURG = { background: "#8B1E1E" };

const MATRIX_DEPARTMENTS = [
  "Four Lanes", "Ikot Ekpene", "Ikpa Road",
  "Bread Unit", "Gelato Unit", "Admin", "Steward",
];

function Wh({ value, onChange, list, disabled, testId, placeholder = "Select warehouse…" }) {
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>{list.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function ItemRows({ lines, setLines, items, prefix }) {
  const setLine = (i, k, v) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));
  const add = () => setLines((ls) => [...ls, { product_id: "", quantity: 1 }]);
  const rm = (i) => setLines((ls) => ls.filter((_, idx) => idx !== i));
  return (
    <div className="mt-2">
      <div className="flex items-center justify-between mb-2"><Label>Items</Label>
        <Button variant="outline" size="sm" onClick={add} data-testid={`${prefix}-add-line`}><Plus size={14} className="mr-1" /> Add</Button></div>
      <div className="space-y-2">{lines.map((l, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 items-center">
          <div className="col-span-8"><Select value={l.product_id} onValueChange={(v) => setLine(i, "product_id", v)}>
            <SelectTrigger data-testid={`${prefix}-item-${i}`}><SelectValue placeholder="Ingredient…" /></SelectTrigger>
            <SelectContent>{items.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select></div>
          <Input className="col-span-3" type="number" value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} data-testid={`${prefix}-qty-${i}`} />
          <button className="col-span-1 flex justify-center" style={{ color: "#A52222" }} onClick={() => rm(i)} data-testid={`${prefix}-remove-${i}`}><Trash2Icon size={16} /></button>
        </div>))}
      </div>
    </div>
  );
}

/* Multi-department dispatch matrix (one row per item × 7 department columns). */
function IssueMatrix({ rows, setRows, items, prefix = "issue-matrix" }) {
  const setCell = (i, dept, val) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, cells: { ...r.cells, [dept]: val } } : r)));
  const setProduct = (i, v) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, product_id: v } : r)));
  const add = () => setRows((rs) => [...rs, { product_id: "", cells: {} }]);
  const rm = (i) => setRows((rs) => rs.filter((_, idx) => idx !== i));
  return (
    <div className="mt-2">
      <div className="flex items-center justify-between mb-2">
        <Label>Departmental Allocation Matrix</Label>
        <Button variant="outline" size="sm" onClick={add} data-testid={`${prefix}-add-row`}><Plus size={14} className="mr-1" /> Add Item</Button>
      </div>
      <div className="overflow-x-auto rounded border" style={{ borderColor: "#e6dccf" }}>
        <table className="w-full text-xs">
          <thead>
            <tr style={{ background: "#faf6f0" }}>
              <th className="text-left px-2 py-2 min-w-[180px]">Item</th>
              {MATRIX_DEPARTMENTS.map((d) => (
                <th key={d} className="px-2 py-2 text-right" style={{ color: d === "Admin" || d === "Steward" ? "#8B1E1E" : "#2c2c2c" }}>{d}</th>
              ))}
              <th className="px-1 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t" style={{ borderColor: "#f0e8dc" }}>
                <td className="px-2 py-1.5">
                  <Select value={r.product_id} onValueChange={(v) => setProduct(i, v)}>
                    <SelectTrigger data-testid={`${prefix}-item-${i}`}><SelectValue placeholder="Ingredient…" /></SelectTrigger>
                    <SelectContent>{items.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                {MATRIX_DEPARTMENTS.map((d) => (
                  <td key={d} className="px-1 py-1.5">
                    <Input type="number" min="0" step="0.01" value={r.cells[d] ?? ""}
                      onChange={(e) => setCell(i, d, e.target.value)}
                      className="text-right h-8 px-2"
                      data-testid={`${prefix}-${i}-${d.replace(/\s+/g, "-").toLowerCase()}`} />
                  </td>
                ))}
                <td className="px-1">
                  <button style={{ color: "#A52222" }} onClick={() => rm(i)} data-testid={`${prefix}-remove-${i}`}><Trash2Icon size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] mt-1.5" style={{ color: "#a58f7c" }}>
        Non-production dispatches (Admin, Steward) auto-post to <b>6700 Administrative Expenses</b>. Kitchen/production dispatches post to <b>5000 COGS</b>.
      </p>
    </div>
  );
}

export default function StockActions({ warehouses, items, reasonCodes, scopedLoc, onDone }) {
  const [dlg, setDlg] = useState(null); // intake|adjust|waste|issue|transfer
  const [saving, setSaving] = useState(false);
  const [newItems, setNewItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const allItems = [...items, ...newItems];
  const myWh = scopedLoc ? warehouses.find((w) => w.location_id === scopedLoc) : warehouses[0];
  const scopedList = scopedLoc ? warehouses.filter((w) => w.location_id === scopedLoc) : warehouses;

  // shared form state
  const [wh, setWh] = useState("");
  const [to, setTo] = useState("");
  const [lines, setLines] = useState([{ product_id: "", quantity: 1 }]);
  const [reason, setReason] = useState("count_correction");
  const [note, setNote] = useState("");
  // matrix issuance state
  const [matrixRows, setMatrixRows] = useState([{ product_id: "", cells: {} }]);
  const [eventName, setEventName] = useState("");
  const [excludeVip, setExcludeVip] = useState(false);
  const [vipRecipient, setVipRecipient] = useState("");
  // intake specific
  const [intake, setIntake] = useState({ product_id: "", quantity: "", unit_cost: "", factor: "1", batch_no: "", expiry: "" });

  const open = (which) => {
    setWh(myWh?.id || ""); setTo(""); setLines([{ product_id: "", quantity: 1 }]);
    setReason(which === "waste" ? "spoilage" : "count_correction"); setNote("");
    setMatrixRows([{ product_id: "", cells: {} }]);
    setEventName(""); setExcludeVip(false); setVipRecipient("");
    setIntake({ product_id: "", quantity: "", unit_cost: "", factor: "1", batch_no: "", expiry: "" });
    setDlg(which);
  };
  const close = () => setDlg(null);
  const done = (msg) => { toast.success(msg); close(); onDone && onDone(); };
  const onItemCreated = (p) => { setNewItems((n) => [...n, p]); setIntake((s) => ({ ...s, product_id: p.id })); onDone && onDone(); };

  const submitIntake = async () => {
    if (!wh || !intake.product_id || Number(intake.quantity) <= 0) { toast.error("Item, warehouse and quantity are required"); return; }
    setSaving(true);
    try { await api.post("/inventory/intake", { warehouse_id: wh, product_id: intake.product_id, quantity: Number(intake.quantity), unit_cost: Number(intake.unit_cost || 0), factor: Number(intake.factor || 1), batch_no: intake.batch_no, expiry: intake.expiry || null, note }); done("Stock received"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  const validLines = () => lines.filter((l) => l.product_id && Number(l.quantity) > 0).map((l) => ({ product_id: l.product_id, quantity: Number(l.quantity) }));
  const submitAdjust = async () => {
    const its = validLines(); if (!wh || !its.length) { toast.error("Warehouse and at least one item required"); return; }
    setSaving(true);
    try { await api.post("/inventory/adjustment", { warehouse_id: wh, reason_code: reason, note, items: its }); done("Adjustment posted"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  const submitWaste = async () => {
    const its = validLines(); if (!wh || !its.length) { toast.error("Warehouse and at least one item required"); return; }
    setSaving(true);
    try { await api.post("/inventory/waste", { warehouse_id: wh, reason_code: reason, note, items: its }); done("Waste recorded"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const buildMatrixPayload = () => {
    const dept_matrix = {};
    for (const row of matrixRows) {
      if (!row.product_id) continue;
      for (const [dept, val] of Object.entries(row.cells)) {
        const qty = Number(val);
        if (!qty || qty <= 0) continue;
        if (!dept_matrix[dept]) dept_matrix[dept] = [];
        dept_matrix[dept].push({ product_id: row.product_id, quantity: qty });
      }
    }
    return dept_matrix;
  };
  const submitIssue = async () => {
    const matrix = buildMatrixPayload();
    const totalLines = Object.values(matrix).reduce((n, arr) => n + arr.length, 0);
    if (!wh || totalLines === 0) { toast.error("Warehouse and at least one dispatch cell required"); return; }
    if (excludeVip && !vipRecipient.trim()) { toast.error("VIP exclusion needs a recipient name"); return; }
    setSaving(true);
    try {
      const res = await api.post("/inventory/issuance", {
        warehouse_id: wh, department_matrix: matrix,
        event_name: eventName || null,
        exclude_vip: excludeVip,
        vip_recipient: vipRecipient || null,
        note,
      });
      const journal = res?.data?.journal_number ? ` · Journal ${res.data.journal_number}` : "";
      done(`Stock issued${journal}`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  const submitTransfer = async () => {
    const its = validLines(); if (!wh || !to || wh === to) { toast.error("Pick two different warehouses"); return; }
    if (!its.length) { toast.error("Add at least one item"); return; }
    setSaving(true);
    try { await api.post("/inventory/transfer", { from_warehouse: wh, to_warehouse: to, items: its }); done("Stock transferred"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const Foot = ({ onSave, testId, label }) => (
    <DialogFooter><Button variant="outline" onClick={close}>Cancel</Button>
      <Button onClick={onSave} disabled={saving} data-testid={testId} style={BURG} className="text-white">{saving ? "Saving…" : label}</Button></DialogFooter>
  );

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={() => setShowNew(true)} data-testid="action-new-item" variant="outline"><FilePlus2 size={16} className="mr-1" /> New Item</Button>
        <Button onClick={() => open("intake")} data-testid="action-intake" style={BURG} className="text-white"><PackagePlus size={16} className="mr-1" /> Intake</Button>
        <Button onClick={() => open("adjust")} data-testid="action-adjust" variant="outline"><SlidersHorizontal size={16} className="mr-1" /> Adjust</Button>
        <Button onClick={() => open("transfer")} data-testid="action-transfer" variant="outline"><ArrowLeftRight size={16} className="mr-1" /> Transfer</Button>
        <Button onClick={() => open("waste")} data-testid="action-waste" variant="outline"><Trash size={16} className="mr-1" /> Waste</Button>
        <Button onClick={() => open("issue")} data-testid="action-issue" variant="outline"><PackageMinus size={16} className="mr-1" /> Issue</Button>
      </div>

      {/* Intake */}
      <Dialog open={dlg === "intake"} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-brand">Stock Intake</DialogTitle><DialogDescription>Receive raw materials directly into a store. Creates a FIFO batch.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-1">
            <div className="space-y-1.5"><Label>Warehouse</Label><Wh value={wh} onChange={setWh} list={scopedList} disabled={!!scopedLoc} testId="intake-wh" /></div>
            <div className="space-y-1.5"><div className="flex items-center justify-between"><Label>Item</Label>
                <button type="button" className="text-xs font-medium inline-flex items-center gap-1" style={{ color: "#8B1E1E" }} onClick={() => setShowNew(true)} data-testid="intake-new-item"><Plus size={12} /> New item</button></div>
              <Select value={intake.product_id} onValueChange={(v) => setIntake((s) => ({ ...s, product_id: v }))}><SelectTrigger data-testid="intake-item"><SelectValue placeholder="Item…" /></SelectTrigger>
                <SelectContent>{allItems.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label>Quantity (purchase UOM)</Label><Input type="number" value={intake.quantity} onChange={(e) => setIntake((s) => ({ ...s, quantity: e.target.value }))} data-testid="intake-qty" /></div>
            <div className="space-y-1.5"><Label>Conversion factor</Label><Input type="number" value={intake.factor} onChange={(e) => setIntake((s) => ({ ...s, factor: e.target.value }))} data-testid="intake-factor" /><span className="text-xs" style={{ color: "#a58f7c" }}>e.g. buy in kg, recipe in g → 1000</span></div>
            <div className="space-y-1.5"><Label>Unit cost (per purchase UOM)</Label><Input type="number" value={intake.unit_cost} onChange={(e) => setIntake((s) => ({ ...s, unit_cost: e.target.value }))} data-testid="intake-cost" /></div>
            <div className="space-y-1.5"><Label>Batch / Lot no.</Label><Input value={intake.batch_no} onChange={(e) => setIntake((s) => ({ ...s, batch_no: e.target.value }))} placeholder="auto" data-testid="intake-batch" /></div>
            <div className="space-y-1.5 col-span-2"><Label>Expiry date (optional)</Label><Input type="date" value={intake.expiry} onChange={(e) => setIntake((s) => ({ ...s, expiry: e.target.value }))} data-testid="intake-expiry" /></div>
          </div>
          <Foot onSave={submitIntake} testId="intake-save" label="Receive Stock" />
        </DialogContent>
      </Dialog>

      {/* Adjust / Waste share the multi-item form */}
      <Dialog open={dlg === "adjust"} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-brand">Stock Adjustment</DialogTitle><DialogDescription>Correct on-hand quantities. Positive adds, negative removes (FIFO). Reason is audited.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-1">
            <div className="space-y-1.5"><Label>Warehouse</Label><Wh value={wh} onChange={setWh} list={scopedList} disabled={!!scopedLoc} testId="adjust-wh" /></div>
            <div className="space-y-1.5"><Label>Reason code</Label>
              <Select value={reason} onValueChange={setReason}><SelectTrigger data-testid="adjust-reason"><SelectValue /></SelectTrigger>
                <SelectContent>{reasonCodes.map((r) => <SelectItem key={r} value={r}>{titleCase(r)}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <ItemRows lines={lines} setLines={setLines} items={allItems} prefix="adjust" />
          <div className="space-y-1.5 mt-2"><Label>Note</Label><Textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="adjust-note" /></div>
          <Foot onSave={submitAdjust} testId="adjust-save" label="Post Adjustment" />
        </DialogContent>
      </Dialog>

      <Dialog open={dlg === "waste"} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-brand">Record Waste / Spoilage</DialogTitle><DialogDescription>Removes stock via FIFO and records the loss reason.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-1">
            <div className="space-y-1.5"><Label>Warehouse</Label><Wh value={wh} onChange={setWh} list={scopedList} disabled={!!scopedLoc} testId="waste-wh" /></div>
            <div className="space-y-1.5"><Label>Reason code</Label>
              <Select value={reason} onValueChange={setReason}><SelectTrigger data-testid="waste-reason"><SelectValue /></SelectTrigger>
                <SelectContent>{reasonCodes.map((r) => <SelectItem key={r} value={r}>{titleCase(r)}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <ItemRows lines={lines} setLines={setLines} items={allItems} prefix="waste" />
          <div className="space-y-1.5 mt-2"><Label>Note</Label><Textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="waste-note" /></div>
          <Foot onSave={submitWaste} testId="waste-save" label="Record Waste" />
        </DialogContent>
      </Dialog>

      {/* Multi-department issuance matrix */}
      <Dialog open={dlg === "issue"} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-brand">Multi-Department Stock Issuance Matrix</DialogTitle>
            <DialogDescription>Dispatch one or more ingredients across the seven recipient departments. Admin / Steward columns auto-route to Administrative Expenses; the rest post to COGS.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-1">
            <div className="space-y-1.5"><Label>Warehouse</Label><Wh value={wh} onChange={setWh} list={scopedList} disabled={!!scopedLoc} testId="issue-wh" /></div>
            <div className="space-y-1.5">
              <Label>Catering / Event Tag</Label>
              <Input value={eventName} onChange={(e) => setEventName(e.target.value)} placeholder="e.g. Chidera 40th Birthday" data-testid="issue-event-name" />
            </div>
          </div>
          <IssueMatrix rows={matrixRows} setRows={setMatrixRows} items={allItems} prefix="issue-matrix" />
          <div className="mt-3 flex items-start gap-3 p-3 rounded border" style={{ borderColor: "#e6dccf", background: "#fbf8f2" }}>
            <div className="flex items-center gap-2 pt-1">
              <Checkbox id="exclude-vip" checked={excludeVip} onCheckedChange={(v) => setExcludeVip(!!v)} data-testid="issue-exclude-vip" />
              <Label htmlFor="exclude-vip" className="font-medium">VIP Management Issue Exclusion</Label>
            </div>
            {excludeVip && (
              <div className="flex-1">
                <Input value={vipRecipient} onChange={(e) => setVipRecipient(e.target.value)} placeholder="VIP recipient (e.g. Mrs Binta)" data-testid="issue-vip-recipient" />
                <p className="text-[11px] mt-1" style={{ color: "#a58f7c" }}>When ticked, every line on this issuance is booked to 6700 Administrative Expenses, tagged with the recipient.</p>
              </div>
            )}
          </div>
          <div className="space-y-1.5 mt-3"><Label>Note</Label><Textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="issue-note" /></div>
          <Foot onSave={submitIssue} testId="issue-save" label="Dispatch Stock Matrix" />
        </DialogContent>
      </Dialog>

      {/* Transfer */}
      <Dialog open={dlg === "transfer"} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-brand">Transfer Stock Between Outlets</DialogTitle><DialogDescription>Creates linked out/in movements sharing one transfer reference.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-1">
            <div className="space-y-1.5"><Label>From</Label><Wh value={wh} onChange={setWh} list={scopedList} disabled={!!scopedLoc} testId="transfer-from" /></div>
            <div className="space-y-1.5"><Label>To</Label><Wh value={to} onChange={setTo} list={warehouses.filter((w) => w.id !== wh)} testId="transfer-to" /></div>
          </div>
          <ItemRows lines={lines} setLines={setLines} items={allItems} prefix="transfer" />
          <Foot onSave={submitTransfer} testId="transfer-save" label="Transfer" />
        </DialogContent>
      </Dialog>

      <NewItemDialog open={showNew} onOpenChange={setShowNew} onCreated={onItemCreated} />
    </>
  );
}
